import React, { useRef } from 'react'
import * as d3 from 'd3'
import useSize from 'hooks/useSize'
import styled from 'styled-components'
import { fillMissingValues } from 'components/FortuneChart/component'

const isMobile = window.innerWidth < 900

interface FortunesData {
  year: number
  fortune: number
}

interface MiniFortuneChartProps {
  rawData: FortunesData[]
}

export const InlineFortuneChart = ({ rawData }: MiniFortuneChartProps) => {
  const data = fillMissingValues(rawData)
  const svgRef = useRef<SVGSVGElement>(null)
  const [container, size] = useSize()
  const width = size.width
  const height = width * (isMobile ? 0.5 : 0.25)
  const gutterTop = 2
  const gutterRight = 4
  const gutterLeft = 4
  const gutterBottom = 2
  const plotArea = {
    top: gutterTop,
    right: width - gutterRight,
    left: gutterLeft,
    bottom: height - gutterBottom,
  }

  const [minFortune, maxFortune] = [
    d3.min(data, (d) => d.fortune) || 0,
    d3.max(data, (d) => d.fortune) || 0,
  ]

  const [minYear, maxYear] = [
    d3.min(data, (d) => d.year) || 0,
    d3.max(data, (d) => d.year) || 0,
  ]

  const xAxisScale = React.useMemo(
    () =>
      d3
        .scaleLinear()
        .domain([minYear, maxYear])
        .range([plotArea.left, plotArea.right]),
    [size, data]
  )

  const yAxisScale = React.useMemo(
    () =>
      d3
        .scaleLinear()
        .domain([minFortune, maxFortune])
        .range([plotArea.bottom, plotArea.top]),
    [size, data]
  )

  const xFunction = (d: FortunesData) => xAxisScale(d.year) || 0
  const yFunction = (d: FortunesData) => yAxisScale(d.fortune) || 0

  const lineFunction = React.useMemo(
    () =>
      d3
        .line<FortunesData>()
        .x((d) => xFunction(d))
        .y((d) => yFunction(d)),
    [size, data]
  )

  return (
    <Container ref={container}>
      <svg
        ref={svgRef}
        width={width}
        height={height}
        style={{ position: 'relative' }}
      >
        <Path d={lineFunction(data) || undefined} />
      </svg>
    </Container>
  )
}

const Container = styled.div`
  position: relative;
`

const Path = styled.path`
  fill: none;
  stroke: ${({ theme }) => theme.colorMain};
  stroke-width: 2px;
  stroke-linejoin: round;
  stroke-linecap: round;
`